'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Shared site header for all public pages (landing, pricing, updates...).
 *
 * Single source of truth for the top nav so every page shares the same logo,
 * font, theme and links. Fixed + backdrop-blur to match the landing page —
 * content pages must add top padding (~pt-20) so content clears it.
 *
 * - onLoginClick: only the landing page passes this (opens the auth modal in
 *   place). Everywhere else "login / register" links to /?auth=login, which the
 *   landing reads on mount to open the same modal.
 * - className: lets the landing pass its mount fade-in animation classes.
 */
export default function SiteHeader({
  onLoginClick,
  className = '',
}: {
  onLoginClick?: () => void;
  className?: string;
}) {
  const { user } = useAuth();

  const linkCls =
    'text-xs sm:text-sm text-white/70 hover:text-white/90 transition-colors duration-200 font-medium whitespace-nowrap';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 bg-transparent ${className}`}
    >
      {/* data-nosnippet: keep the logo alt + nav links out of Google's snippet so
          they don't get scraped into the search description (e.g. "pricingupdates"). */}
      <div data-nosnippet className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link href="/" aria-label="Ultragoon home" className="flex items-center">
          <Image
            src="/ultragoon.svg"
            alt="ultragoon"
            width={80}
            height={18}
            priority
            className="h-4 sm:h-[18px] w-auto"
          />
        </Link>

        <div className="flex items-center gap-3 sm:gap-6">
          <Link href="/community" className={linkCls}>
            community
          </Link>
          <Link href="/pricing" className={linkCls}>
            pricing
          </Link>
          <Link href="/updates" className={linkCls}>
            updates
          </Link>
          {user ? (
            <Link href="/dashboard" className={linkCls}>
              dashboard
            </Link>
          ) : onLoginClick ? (
            <button onClick={onLoginClick} className={`${linkCls} cursor-pointer`}>
              login / register
            </button>
          ) : (
            <Link href="/?auth=login" className={linkCls}>
              login / register
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
